import { FormikProps } from "formik";
import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import * as Yup from "yup";

import { Button } from "@/components/Button";
import { FormikError, VALIDATION_TYPES } from "@/components/Formik";
import { Header } from "@/components/Header";
import { Icon } from "@/components/Icon";
import { LoadingSpinner } from "@/components/Loading";
import { Text } from "@/components/Text";
import { MEDIA_SIZE_SM } from "@/core/media";
import { IConsultantProfileLanguage } from "@/data/consultant-profiles";
import { ILanguage } from "@/data/languages";
import { Flex, Group } from "@/eworkitComponents";
import { GroupedLanguagesSelectFormik, ICodelistGenericSelectParams } from "@/views/components/Codelists";
import { FormikCodelistGenericSelect } from "@/views/components/Formik";
import { SuggestedLanguagesList } from "@/views/components/Languages/SuggestedLanguagesList";

import { SuggestedSection } from "./SuggestedSection";

const LANGUAGE_LEVELS_CODELIST_QUERY: ICodelistGenericSelectParams = { codelistName: "language-levels" };

export interface IProfileLanguagesSectionValues {
  languages: IConsultantProfileLanguage[];
}

export const profileLanguagesSectionValidationSchema = (required?: boolean) =>
  Yup.object()
    .required()
    .shape({
      languages: Yup.array()
        .of(
          Yup.object().shape({
            language: Yup.string().required(VALIDATION_TYPES.REQUIRED),
            level: Yup.string().required(VALIDATION_TYPES.REQUIRED),
          }),
        )
        .test({
          message: VALIDATION_TYPES.REQUIRED,
          test: (val: IConsultantProfileLanguage[]) => (required ? val.length > 0 : true),
        }),
    });

interface IProfileLanguagesSectionProps {
  form: FormikProps<IProfileLanguagesSectionValues>;
  suggestedLanguages?: ILanguage[];
  required?: boolean;
}

export const ProfileLanguagesSection: FC<IProfileLanguagesSectionProps> = ({ form, suggestedLanguages, required }) => {
  const { t } = useTranslation("session");

  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const addLanguage = useCallback(
    (language: Partial<IConsultantProfileLanguage>) => {
      form.setFieldValue("languages", [...form.values.languages, language]);
    },
    [form],
  );

  const removeLanguage = useCallback(
    (language: ILanguage) => {
      if (required && form.values.languages.length === 1 && form.values.languages[0].language === language) {
        form.setFieldValue("languages", [{}]);
      } else {
        form.setFieldValue("languages", [...form.values.languages.filter(l => l.language !== language)]);
      }
    },
    [required, form],
  );

  const handleSuggestedLanguageClick = useCallback(
    (language: ILanguage) => {
      const emptyLanguageIndex = form.values.languages.findIndex(l => l.language === undefined);
      if (emptyLanguageIndex > -1) {
        form.setFieldValue("languages", [
          ...form.values.languages.slice(0, emptyLanguageIndex),
          { language },
          ...form.values.languages.slice(emptyLanguageIndex + 1),
        ]);
        return;
      }

      addLanguage({ language });
    },
    [form],
  );

  const handleCheckedSuggestedLanguageClick = useCallback(
    (language: ILanguage) => {
      removeLanguage(language);
    },
    [removeLanguage],
  );

  const handleAddNextClick = useCallback(() => {
    addLanguage({});
  }, [addLanguage]);

  const handleDeleteLanguageClick = useCallback(
    (index: number) => {
      form.setFieldValue("languages", [
        ...form.values.languages.slice(0, index),
        ...form.values.languages.slice(index + 1),
      ]);
    },
    [form],
  );

  return (
    <Flex direction="column" innerMargin="sm">
      <Header size="sm" color="black" weight="bold">
        {t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.HEADER_LANGUAGES")}
      </Header>

      <Flex grow={1} direction="column" innerMargin="md">
        <Flex direction="column" innerMargin="md">
          {form.values.languages.map((lang, langIndex) => (
            <Flex
              key={lang.language || langIndex}
              grow={1}
              innerMargin={isSmallScreen ? "md" : "xl"}
              direction={isSmallScreen ? "column" : "row"}
              align="flex-start">
              <Group
                direction="column"
                label={
                  langIndex === 0 || isSmallScreen
                    ? t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.LABEL_LANGUAGE")
                    : undefined
                }
                width={{ xxs: 12, md: 6 }}>
                <GroupedLanguagesSelectFormik
                  value={lang.language}
                  name={`languages[${langIndex}].language`}
                  testName="languge"
                  size="md"
                  placeholder={t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.PLACEHOLDER_LANGUAGE")}
                  isClearable={false}
                  filterLanguages={form.values.languages.map(l => l.language)}
                  required
                />
                <FormikError name={`languages[${langIndex}].language`} data-test="error-languge" />
              </Group>

              <Group
                direction="column"
                label={
                  langIndex === 0 || isSmallScreen
                    ? t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.LABEL_LANGUAGE_LEVEL")
                    : undefined
                }
                width={{ xxs: 12, md: 6 }}>
                <Flex direction="row" innerMargin="sm">
                  <Flex grow={1} direction="column">
                    <FormikCodelistGenericSelect
                      simpleValue
                      value={lang.level}
                      name={`languages[${langIndex}].level`}
                      testName="languge-level"
                      query={LANGUAGE_LEVELS_CODELIST_QUERY}
                      size="md"
                      placeholder={t(
                        "CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.PLACEHOLDER_LANGUAGE_LEVEL",
                      )}
                      isClearable={false}
                      required
                    />
                    <FormikError name={`languages[${langIndex}].level`} data-test="error-languge-level" />
                  </Flex>

                  {(!required || form.values.languages.length > 1) && (
                    <Button variant="clean" onClick={() => handleDeleteLanguageClick(langIndex)}>
                      <Icon color="grey2" icon="close" size="lg" />
                    </Button>
                  )}
                </Flex>
              </Group>
            </Flex>
          ))}

          <Flex>
            <Button variant="secondary" display="inline-block" onClick={handleAddNextClick}>
              <Text>{t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.BUTTON_ADD_NEXT_LANGUAGE")}</Text>
            </Button>
          </Flex>
        </Flex>

        {suggestedLanguages ? (
          <SuggestedSection header={t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.SUGGESTED_LANGUAGES")}>
            <SuggestedLanguagesList
              onClick={handleSuggestedLanguageClick}
              suggestedLanguages={suggestedLanguages}
              onCheckedClick={handleCheckedSuggestedLanguageClick}
              selectedLanguages={form.values.languages.map(l => l.language)}
            />
          </SuggestedSection>
        ) : (
          <Flex align="center" justify="center" grow={1} padding={["lg"]}>
            <LoadingSpinner size="lg" variant="primary" />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
